/**
 * Credit Settlement – Corporate Settlement (provider list).
 * GET /histopathology/billing/corporate/?from_date&to_date&search&bill_type
 * Action navigates to /HistopathologyCreditSettlement/Corporate/Payment with provider state.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Paper, Typography, TextField, IconButton, Tooltip, MenuItem, FormControl, InputLabel, Select } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { canEditTab } from "../../Utility/Services/RoleAccessService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import CommonValidation from "../../Utility/CommonValidation";
import { CommonGridHeader } from "../../Components/Common Components/CommonComponents";
import CommonDatePicker from "../../Components/Common Components/CommonDatePicker";
import { CurrencySymbol } from "../../Utility/Constants";
import "../../Styles/Configuration.css";

const today = () => DateTime.now().toFormat("yyyy-MM-dd");

const SummaryCard = ({ label, value }) => (
  <Paper elevation={1} sx={{ px: 2, py: 1.2, minWidth: "11vw", borderRadius: "8px" }}>
    <Typography sx={{ fontSize: "1.05vw", fontWeight: 700, color: "#1a1a1a" }}>{value}</Typography>
    <Typography sx={{ fontSize: "0.75vw", color: "#666" }}>{label}</Typography>
  </Paper>
);

export default function CorporateProvider() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [dateIndex, setDateIndex] = useState(1);
  const [search, setSearch] = useState("");
  const [billType, setBillType] = useState("outstanding");
  const [totals, setTotals] = useState({ count: 0, received: 0, outstanding: 0 });
  const [msg, setMsg] = useState({ err: false, errText: "", ok: false, okText: "" });

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (fromDate) params.append("from_date", fromDate);
    if (toDate) params.append("to_date", toDate);
    if (billType) params.append("bill_type", billType);
    if (search) params.append("search", search);
    const url = `${Serviceurls.HISTOPATHOLOGY_CREDIT_SETTLEMENT_CORPORATE}?${params.toString()}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success") {
          setRows(res.data.data ?? []);
          const t = res.data.totals || {};
          setTotals({
            count: +t.total_count || 0,
            received: +t.total_received || 0,
            outstanding: +t.total_outstanding || 0,
          });
        } else {
          setRows([]);
          setTotals({ count: 0, received: 0, outstanding: 0 });
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setRows([]);
        setTotals({ count: 0, received: 0, outstanding: 0 });
        setMsg({ err: true, errText: err?.response?.data?.message || err?.message, ok: false, okText: "" });
      });
  }, [fromDate, toDate, billType, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const goToPayment = (row) => {
    navigate("/HistopathologyCreditSettlement/Corporate/Payment", {
      state: {
        corporate_id: row.corporate_id ?? row.id,
        corporate_name: row.company_name ?? row.corporate_name,
        bills_count: row.bills_count ?? row.no_of_admissions,
        received_amount: row.received_amount,
        outstanding_amount: row.outstanding_amount,
        payable_amount: row.payable_amount,
        from_date: fromDate,
        to_date: toDate,
        bill_type: billType,
      },
    });
  };

  const columns = [
    { field: "sno", headerName: "S.No", flex: 0.4, minWidth: 60, sortable: false },
    {
      field: "company_name", headerName: "Corporate Provider", flex: 1.6, minWidth: 200,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <Typography variant="body2" fontWeight={600}>{p.row.company_name || p.row.corporate_name || "-"}</Typography>
          {(p.row.mobile || p.row.email) && (
            <Typography variant="caption" color="text.secondary">
              {[p.row.mobile, p.row.email].filter(Boolean).join(" | ")}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "bills_count", headerName: "No Of Bills", flex: 0.7, minWidth: 100,
      align: "center", headerAlign: "center",
      valueGetter: (value, row) => row.bills_count ?? row.no_of_admissions ?? 0,
    },
    {
      field: "payable_amount", headerName: `Bill Amount (${CurrencySymbol})`, flex: 1, minWidth: 130,
      align: "right", headerAlign: "right",
      renderCell: (p) => CommonValidation.formatAmount(p.row.payable_amount ?? 0),
    },
    {
      field: "received_amount", headerName: `Received Amount (${CurrencySymbol})`, flex: 1.1, minWidth: 150,
      align: "right", headerAlign: "right",
      renderCell: (p) => CommonValidation.formatAmount(p.row.received_amount ?? 0),
    },
    {
      field: "outstanding_amount", headerName: `Outstanding Amount (${CurrencySymbol})`, flex: 1.1, minWidth: 160,
      align: "right", headerAlign: "right",
      renderCell: (p) => (
        <Typography sx={{ fontSize: "0.85vw", color: +p.row.outstanding_amount > 0 ? "#d32f2f" : "#2e7d32", fontWeight: 600 }}>
          {CommonValidation.formatAmount(p.row.outstanding_amount ?? 0)}
        </Typography>
      ),
    },
    {
      field: "action", headerName: "Action", sortable: false, flex: 0.5, minWidth: 80,
      align: "center", headerAlign: "center",
      renderCell: (p) => {
        const canEdit = canEditTab("Corporate Settlement");
        return (
          <Tooltip title={canEdit ? "Settlement" : "No edit access"}>
            <span>
              <IconButton size="small" onClick={() => goToPayment(p.row)} disabled={!canEdit} color={canEdit ? "primary" : "default"}>
                <AccountBalanceWalletOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: "0.8vw", height: "100%", display: "flex", flexDirection: "column", gap: "0.8vw", overflow: "hidden", minHeight: 0 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <SummaryCard label="Total Count" value={totals.count} />
        <SummaryCard label="Total Received" value={`${CurrencySymbol} ${CommonValidation.formatAmount(totals.received)}`} />
        <SummaryCard label="Total OutStanding" value={`${CurrencySymbol} ${CommonValidation.formatAmount(totals.outstanding)}`} />
        <Box sx={{ flexGrow: 1 }} />
        <FormControl size="small" sx={{ minWidth: "10vw" }}>
          <InputLabel>Bill Type</InputLabel>
          <Select label="Bill Type" value={billType} onChange={(e) => setBillType(e.target.value)}>
            <MenuItem value="outstanding">Outstanding</MenuItem>
            <MenuItem value="history">History</MenuItem>
            <MenuItem value="complete">Complete</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ minWidth: "12vw" }}>
          <CommonDatePicker
            FromDate={fromDate}
            ToDate={toDate}
            DateIndex={dateIndex}
            HideAllMenu
            removeMarginRight
            SelectedDates={(from, to, idx) => {
              setFromDate(from);
              setToDate(to);
              setDateIndex(idx);
            }}
          />
        </Box>
        <TextField size="small" placeholder="Search" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: "14vw" }} />
      </Stack>
      <Paper sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex" }}>
        <DataGrid
          rows={rows.map((r, i) => ({ ...r, id: r.corporate_id ?? r.id ?? i, sno: i + 1 }))}
          columns={columns}
          loading={loading}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
          pageSizeOptions={[10, 20, 30]}
          disableRowSelectionOnClick
          rowHeight={50}
          columnHeaderHeight={42}
          slots={{
            toolbar: CommonGridHeader.CommonToolBar,
            noRowsOverlay: () => <Stack className="eMed_conf_nodata">No Records Found</Stack>,
          }}
          sx={{ border: 0, flex: 1, minWidth: 0 }}
        />
      </Paper>
      {msg.err && <ToastMsg severity="error" msg={msg.errText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
      {msg.ok && <ToastMsg severity="success" msg={msg.okText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
    </Box>
  );
}
